'use client'

import { useState, useEffect } from 'react'
import {
  hasStoredSignupAccess,
  shouldSyncSignupAccessFromStorageEvent,
  SIGNUP_ACCESS_STORED_EVENT,
  SIGNUPS_OPEN,
} from '@/lib/signupAccessStorage'

/**
 * Reactive view of hasStoredSignupAccess() for marketing surfaces deciding
 * between "Sign up" and "Request Access". Starts false so statically
 * prerendered pages hydrate cleanly, then re-checks on mount, when a code is
 * stored in this tab (SIGNUP_ACCESS_STORED_EVENT covers both referral and
 * promo writes), on cross-tab storage changes, and on refocus.
 */
export function useStoredSignupAccess(): boolean {
  // SIGNUPS_OPEN is build-time constant, so initializing true is hydration-safe
  // (server and client agree) and the access CTAs render without a flash.
  const [hasAccess, setHasAccess] = useState(SIGNUPS_OPEN)

  useEffect(() => {
    if (SIGNUPS_OPEN) return
    const update = () => setHasAccess(hasStoredSignupAccess())
    const handleStorage = (event: StorageEvent) => {
      if (shouldSyncSignupAccessFromStorageEvent(event.key)) update()
    }

    update()
    window.addEventListener(SIGNUP_ACCESS_STORED_EVENT, update)
    window.addEventListener('storage', handleStorage)
    window.addEventListener('focus', update)

    return () => {
      window.removeEventListener(SIGNUP_ACCESS_STORED_EVENT, update)
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener('focus', update)
    }
  }, [])

  return hasAccess
}
